import React from 'react';
import { Settings } from 'lucide-react';
import type { ProblemType, UserSettings, DailyProgress } from '../../types';
import { PROBLEM_GROUPS, GROUP_ORDER, PROBLEM_DEFINITIONS, DAILY_GOAL } from '../../constants';

// Display groups in the learning-first order defined by GROUP_ORDER.
const ORDERED_GROUPS = GROUP_ORDER.map(
  (id) => PROBLEM_GROUPS.find((g) => g.id === id)!
).filter(Boolean);

interface CategoryGridProps {
  settings: UserSettings;
  dailyProgress: DailyProgress;
  onSelectCategory: (type: ProblemType) => void;
  onOpenSettings: () => void;
}

export const CategoryGrid: React.FC<CategoryGridProps> = ({
  settings,
  dailyProgress,
  onSelectCategory,
  onOpenSettings,
}) => {
  return (
    <div className="h-[100dvh] overflow-hidden bg-[#f4fcf4] font-maru flex flex-col">
      {/* Header */}
      <header className="p-4 flex justify-between bg-white shadow-sm">
        <div className="font-bold text-xl text-[#4CAF50]">植木算マスターPro</div>
        <button onClick={onOpenSettings} className="opacity-30 hover:opacity-60 transition-opacity">
          <Settings />
        </button>
      </header>

      {/* Category Grid by Groups */}
      <main className="flex-1 min-h-0 p-4 overflow-y-auto">
        {ORDERED_GROUPS.map((group) => {
          // Get enabled types in this group
          const enabledTypes = group.types.filter((key) => settings.enabledTypes[key]);
          if (enabledTypes.length === 0) return null;

          return (
            <div key={group.id} className="mb-6">
              {/* Group Header */}
              <div className="flex items-center gap-2 mb-3 px-1">
                <span className="text-lg">{group.icon}</span>
                <h2 className="font-bold text-gray-700">{group.title}</h2>
                <div className="flex-1 h-px bg-gray-200 ml-2" />
              </div>

              {/* Category Buttons */}
              <div className="grid grid-cols-2 gap-3">
                {enabledTypes.map((key) => {
                  const def = PROBLEM_DEFINITIONS[key];
                  const wins = dailyProgress.wins[key] || 0;
                  const progress = Math.min(100, (wins / DAILY_GOAL) * 100);

                  return (
                    <button
                      key={key}
                      onClick={() => onSelectCategory(key)}
                      className="bg-white p-3 rounded-xl shadow-md border-b-4 border-gray-200 active:border-b-0 active:translate-y-1 transition-all flex flex-col items-center gap-2 h-28 justify-center relative overflow-hidden"
                    >
                      <div className="text-2xl font-bold" style={{ color: def.color }}>
                        {def.icon}
                      </div>
                      <div className="text-sm font-bold text-gray-700">{def.title}</div>
                      <div className="text-xs text-gray-400">せいかい: {wins}</div>
                      <div className="absolute bottom-0 left-0 h-1 bg-gray-100 w-full">
                        <div
                          className="h-full transition-all"
                          style={{ width: `${progress}%`, background: def.color }}
                        />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </main>
    </div>
  );
};
